import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { identity } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { listTimezones } from '@repletoryreach/sdk-utils/date';
import { Stack, Card, Title, Button, Text, Group, Badge } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Select, Switch, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';

/**
 * When the business is open (7 October 2026): the weekly hours, closed days
 * and the time zone they are read in. Bookings, ticket response times and the
 * chatbot's opening hours all read this one answer. Every member sees it;
 * owners and admins change it (the service refuses anyone else).
 */

type Slot = { opens: string; closes: string };
type Day = { on: boolean; slots: Slot[] };
type Week = Record<identity.Weekday, Day>;

const MUTED = 'var(--rr-color-text-muted)';
const MAX_SLOTS = 6;
const MAX_CLOSURES = 100;
const LABEL_MAX = 80;

function emptyWeek(): Week {
  return Object.fromEntries(identity.WEEKDAYS.map((d) => [d, { on: false, slots: [] }])) as unknown as Week;
}

function toWeek(slots: identity.BusinessHoursSlot[]): Week {
  const week = emptyWeek();
  for (const s of slots) {
    week[s.day].on = true;
    week[s.day].slots.push({ opens: s.opens, closes: s.closes });
  }
  return week;
}

function fromWeek(week: Week): identity.BusinessHoursSlot[] {
  return identity.WEEKDAYS.flatMap((d) =>
    week[d].on ? week[d].slots.map((s) => ({ day: d, opens: s.opens, closes: s.closes })) : []);
}

/** The first problem with one day, as a catalogue key, or null. Mirrors the service's rules. */
function dayProblem(day: Day): string | null {
  if (!day.on) return null;
  if (day.slots.length === 0) return 'settings.hours.error.noSlots';
  if (day.slots.some((s) => !/^\d{2}:\d{2}$/.test(s.opens) || !/^\d{2}:\d{2}$/.test(s.closes))) return 'settings.hours.error.time';
  if (day.slots.some((s) => s.opens >= s.closes)) return 'settings.hours.error.order';
  const sorted = [...day.slots].sort((a, b) => a.opens.localeCompare(b.opens));
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i]!.opens < sorted[i - 1]!.closes) return 'settings.hours.error.overlap';
  }
  return null;
}

export function BusinessHoursCard() {
  const t = useT();
  const qc = useQueryClient();
  const { user, refresh } = useAuth();
  const canEdit = user?.role == null || identity.isOrgAdmin(user.role);

  const hoursQ = useQuery({ queryKey: ['business-hours'], queryFn: () => identity.getBusinessHours() });
  const saved = hoursQ.data?.data;

  const [timezone, setTimezone] = useState('Africa/Johannesburg');
  const [week, setWeek] = useState<Week>(emptyWeek);
  const [closures, setClosures] = useState<identity.BusinessClosure[]>([]);
  const [newDate, setNewDate] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [closureError, setClosureError] = useState('');

  // Re-seeded whenever the saved answer changes, so a save shows what was stored.
  useEffect(() => {
    if (!saved) return;
    setTimezone(saved.timezone);
    setWeek(toWeek(saved.weekly));
    setClosures(saved.closures);
  }, [saved]);

  const timezones = useMemo(() => {
    const all = listTimezones();
    return all.includes(timezone) ? all : [timezone, ...all];
  }, [timezone]);

  const problems = useMemo(
    () => Object.fromEntries(identity.WEEKDAYS.map((d) => [d, dayProblem(week[d])])) as Record<identity.Weekday, string | null>,
    [week]
  );
  const invalid = identity.WEEKDAYS.some((d) => problems[d] !== null);

  const dirty = !!saved && (
    !saved.configured
    || timezone !== saved.timezone
    || JSON.stringify(fromWeek(week)) !== JSON.stringify(saved.weekly)
    || JSON.stringify(closures) !== JSON.stringify(saved.closures)
  );

  const save = useMutation({
    mutationFn: () => identity.saveBusinessHours({
      timezone,
      weekly: fromWeek(week),
      closures: [...closures].sort((a, b) => a.date.localeCompare(b.date))
    }),
    onSuccess: async (res) => {
      qc.setQueryData(['business-hours'], res);
      // The time zone is the organisation's own, also shown in the card above.
      if (timezone !== user?.org.timezone) await refresh();
      notifySuccess(t('settings.hours.toast.saved'), t('settings.hours.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.hours.title'))
  });

  const setDay = (d: identity.Weekday, next: Partial<Day>) => setWeek((w) => ({ ...w, [d]: { ...w[d], ...next } }));
  const setSlot = (d: identity.Weekday, i: number, next: Partial<Slot>) =>
    setDay(d, { slots: week[d].slots.map((s, j) => (j === i ? { ...s, ...next } : s)) });

  function toggleDay(d: identity.Weekday, on: boolean) {
    setDay(d, { on, slots: on && week[d].slots.length === 0 ? [{ opens: '08:00', closes: '17:00' }] : week[d].slots });
  }

  function addSlot(d: identity.Weekday) {
    const last = week[d].slots[week[d].slots.length - 1];
    // Start the new slot where the last one ends, an hour long, so it is valid as added.
    const opens = last && last.closes < '23:00' ? last.closes : '13:00';
    const [h, m] = opens.split(':').map(Number);
    const closes = `${String(Math.min((h ?? 13) + 1, 23)).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}`;
    setDay(d, { slots: [...week[d].slots, { opens, closes: closes > opens ? closes : '23:59' }] });
  }

  function addClosure() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) { setClosureError(t('settings.hours.closures.dateRequired')); return; }
    if (closures.some((c) => c.date === newDate)) { setClosureError(t('settings.hours.closures.duplicate')); return; }
    if (closures.length >= MAX_CLOSURES) { setClosureError(t('settings.hours.closures.tooMany', { max: MAX_CLOSURES })); return; }
    setClosures((cs) => [...cs, { date: newDate, label: newLabel.trim() }].sort((a, b) => a.date.localeCompare(b.date)));
    setNewDate('');
    setNewLabel('');
    setClosureError('');
  }

  function formatClosure(date: string) {
    const d = new Date(`${date}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? date
      : d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  }

  return (
    <Card withBorder data-testid="business-hours-card" data-guide="org-hours">
      <form onSubmit={(e) => { e.preventDefault(); if (canEdit && !invalid) save.mutate(); }}>
        <Stack gap="md">
          <Stack gap="xs">
            <Group gap="xs" align="center">
              <Title order={4}>{t('settings.hours.title')}</Title>
              {saved && !saved.configured && <Badge size="xs" variant="light" color="gray">{t('settings.hours.defaultBadge')}</Badge>}
            </Group>
            <Text role="bodySmall" c={MUTED}>{t('settings.hours.intro')}</Text>
          </Stack>

          {hoursQ.error && (
            <Alert color="error" role="alert">
              <Group gap="sm" justify="space-between" wrap="wrap">
                <span>{t('settings.hours.loadError')}</span>
                <Button size="xs" variant="default" onClick={() => void hoursQ.refetch()}>{t('action.retry')}</Button>
              </Group>
            </Alert>
          )}
          {!canEdit && <Alert color="info">{t('settings.hours.readOnly')}</Alert>}

          {saved && (
            <>
              <Select
                label={t('settings.hours.timezone')}
                description={t('settings.hours.timezoneHint')}
                value={timezone}
                onChange={(e) => setTimezone(e.currentTarget.value)}
                data={timezones.map((tz) => ({ value: tz, label: tz }))}
                disabled={!canEdit}
              />

              <Stack gap="sm" data-testid="business-hours-week">
                <Text role="inputLabel">{t('settings.hours.weekly')}</Text>
                {identity.WEEKDAYS.map((d) => (
                  <Stack key={d} gap="xs" style={{ paddingBottom: 8, borderBottom: '1px solid var(--rr-color-border)' }}>
                    <Group gap="sm" justify="space-between" wrap="wrap">
                      <Switch
                        label={t(`settings.hours.day.${d}`)}
                        checked={week[d].on}
                        onChange={(on) => toggleDay(d, on)}
                        disabled={!canEdit}
                      />
                      {!week[d].on && <Text role="caption" c={MUTED}>{t('settings.hours.closed')}</Text>}
                    </Group>
                    {week[d].on && week[d].slots.map((s, i) => (
                      <Group key={i} gap="xs" wrap="wrap" align="flex-end">
                        <TextInput
                          type="time"
                          label={t('settings.hours.opens')}
                          value={s.opens}
                          onChange={(e) => setSlot(d, i, { opens: e.currentTarget.value })}
                          disabled={!canEdit}
                        />
                        <TextInput
                          type="time"
                          label={t('settings.hours.closes')}
                          value={s.closes}
                          onChange={(e) => setSlot(d, i, { closes: e.currentTarget.value })}
                          disabled={!canEdit}
                        />
                        {canEdit && week[d].slots.length > 1 && (
                          <Button
                            size="xs"
                            variant="subtle"
                            color="red"
                            aria-label={t('settings.hours.removeSlotFor', { day: t(`settings.hours.day.${d}`) })}
                            onClick={() => setDay(d, { slots: week[d].slots.filter((_, j) => j !== i) })}
                          >
                            {t('action.remove')}
                          </Button>
                        )}
                      </Group>
                    ))}
                    {problems[d] && <Text role="caption" c="var(--rr-color-error)">{t(problems[d]!)}</Text>}
                    {canEdit && week[d].on && week[d].slots.length < MAX_SLOTS && (
                      <div>
                        <Button size="xs" variant="subtle" onClick={() => addSlot(d)}>{t('settings.hours.addSlot')}</Button>
                      </div>
                    )}
                  </Stack>
                ))}
              </Stack>

              <Stack gap="sm" data-testid="business-hours-closures">
                <Stack gap={2}>
                  <Text role="inputLabel">{t('settings.hours.closures')}</Text>
                  <Text role="caption" c={MUTED}>{t('settings.hours.closures.hint')}</Text>
                </Stack>
                {closures.length === 0 ? (
                  <Text role="bodySmall" c={MUTED}>{t('settings.hours.closures.empty')}</Text>
                ) : (
                  <Stack gap="xs">
                    {closures.map((c) => (
                      <Group key={c.date} gap="sm" justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="wrap">
                          <Text role="bodySmall" fw={600}>{formatClosure(c.date)}</Text>
                          {c.label && <Text role="bodySmall" c={MUTED}>{c.label}</Text>}
                        </Group>
                        {canEdit && (
                          <Button
                            size="xs"
                            variant="subtle"
                            color="red"
                            aria-label={t('settings.hours.closures.removeFor', { date: formatClosure(c.date) })}
                            onClick={() => setClosures((cs) => cs.filter((x) => x.date !== c.date))}
                          >
                            {t('action.remove')}
                          </Button>
                        )}
                      </Group>
                    ))}
                  </Stack>
                )}
                {canEdit && (
                  <Group gap="xs" wrap="wrap" align="flex-start">
                    <TextInput
                      type="date"
                      label={t('settings.hours.closures.date')}
                      value={newDate}
                      error={closureError}
                      onChange={(e) => { setNewDate(e.currentTarget.value); setClosureError(''); }}
                    />
                    <TextInput
                      label={t('settings.hours.closures.label')}
                      description={t('settings.hours.closures.labelHint')}
                      placeholder={t('settings.hours.closures.labelPlaceholder')}
                      value={newLabel}
                      maxLength={LABEL_MAX}
                      onChange={(e) => setNewLabel(e.currentTarget.value)}
                    />
                    <div style={{ paddingTop: 24 }}>
                      <Button size="sm" variant="default" onClick={addClosure}>{t('settings.hours.closures.add')}</Button>
                    </div>
                  </Group>
                )}
              </Stack>

              {canEdit && (
                <div>
                  <Button type="submit" loading={save.isPending} disabled={!dirty || invalid}>{t('action.saveChanges')}</Button>
                </div>
              )}
            </>
          )}
        </Stack>
      </form>
    </Card>
  );
}
